import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { Observable, Subject, throwError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import * as Sentry from '@sentry/angular';

import {
  ChatProvider,
  NewChatResponse,
  ChatConfig,
} from './chat-provider.interface';
import { Message, MessageRole } from '../models';
import { environment } from '../../../environments/environment';
import { ErrorService } from './error.service';

type MessageResponse = {
  message: {
    role: 'assistant';
    content: string;
    created_at: string;
  };
  show_survey?: boolean;
  thuisarts_article?: {
    subject: { title: string; link: string };
    situation: { title: string; link: string; revision_date: string };
    content: Array<{
      item: { title: string; value: string; downloads: unknown[] };
      type: string;
      index: number;
      category: { name: string; label: string };
    }>;
    related: unknown;
  };
};

/**
 * HTTP/REST implementation of the ChatProvider interface
 * Uses the Sparringpartner REST API
 */
@Injectable({
  providedIn: 'root',
})
export class HttpChatProviderService extends ChatProvider {
  private readonly apiBaseUrl = environment.apiUrl;
  private readonly _receivedMessage = new Subject<Message>();
  private readonly errorMessage = 'Er ging iets fout. Probeer het later opnieuw.';
  private readonly conversationClosedMessage = 'conversation_closed_by_user';
  private readonly conversationTimedOutMessage = 'conversation_closed_by_timeout';

  constructor(
    private http: HttpClient,
    private errorService: ErrorService
  ) {
    super();
  }

  /**
   * Observable stream of received messages
   */
  receivedMessage(): Observable<Message> {
    return this._receivedMessage.asObservable();
  }

  /**
   * Create a new chat conversation
   * POST /api/v1/chat
   */
  createChat(config?: ChatConfig): Observable<NewChatResponse> {
    let params = new HttpParams();

    if (config?.configuration) {
      params = params.set('configuration', config.configuration);
    }
    if (config?.origin) {
      params = params.set('origin', config.origin);
    }
    if (config?.instance) {
      params = params.set('instance', config.instance);
    }

    return this.http
      .post<{
        id: string;
        created_at: string;
      }>(`${this.apiBaseUrl}/api/v1/chat`, null, { params })
      .pipe(
        map((response) => ({
          id: response.id,
          createdAt: response.created_at,
        })),
        catchError((error: HttpErrorResponse) => this.handleError(error))
      );
  }

  /**
   * Send a message to an existing conversation
   * POST /api/v1/chat/{conversation_id}
   *
   * First emits the user message to receivedMessage(), then sends to API
   * When API responds, emits the bot response to receivedMessage()
   */
  sendMessage(conversationId: string, message: string): Observable<void> {
    // Emit user message immediately for UI feedback
    const userMessage: Message = {
      content: message,
      role: 'user',
      createdAt: new Date().toISOString(),
    };
    this._receivedMessage.next(userMessage);

    // Send message to API
    const url = `${this.apiBaseUrl}/api/v1/chat/${conversationId}`;
    const body = { content: message };

    return this.http.post<MessageResponse>(url, body).pipe(
      tap((response) => {
        // Map API response to Message and emit
        const botMessage = this.mapResponseToMessage(response);
        this._receivedMessage.next(botMessage);
      }),
      map(() => void 0),
      catchError((error: HttpErrorResponse) => this.handleError(error))
    );
  }

  /**
   * Send a conclusion message when the user closes the conversation
   * Posts to the API but does NOT emit via receivedMessage() — this message is never shown in the UI
   */
  sendConclusionMessage(conversationId: string): Observable<void> {
    const url = `${this.apiBaseUrl}/api/v1/chat/${conversationId}`;
    const body = {
      role: 'system',
      content: `[${this.conversationClosedMessage}]`,
      createdAt: new Date().toISOString(),
    };

    return this.http.post(url, body).pipe(
      map(() => void 0),
      catchError((error: HttpErrorResponse) => this.handleError(error))
    );
  }

  /**
   * Send a conclusion message when the conversation is closed due to inactivity timeout
   * Posts to the API but does NOT emit via receivedMessage() — this message is never shown in the UI
   */
  sendTimeoutMessage(conversationId: string): Observable<void> {
    const url = `${this.apiBaseUrl}/api/v1/chat/${conversationId}`;
    const body = {
      role: 'system',
      content: `[${this.conversationTimedOutMessage}]`,
      createdAt: new Date().toISOString(),
    };

    return this.http.post(url, body).pipe(
      map(() => void 0),
      catchError((error: HttpErrorResponse) => this.handleError(error))
    );
  }

  /**
   * Map API response to Message format
   */
  private mapResponseToMessage(response: MessageResponse): Message {
    return {
      content: response.message.content,
      role: response.message.role as MessageRole,
      createdAt: response.message.created_at,
      showSurvey: response.show_survey,
      thuisartsArticle: response.thuisarts_article
        ? {
            subject: response.thuisarts_article.subject,
            situation: response.thuisarts_article.situation,
            content: response.thuisarts_article.content,
          }
        : undefined,
    };
  }

  /**
   * Handle HTTP errors by showing error message and re-throwing
   */
  private handleError(error: HttpErrorResponse): Observable<never> {
    if (error.status >= 400 && error.status < 600) {
      if (error.status >= 500) {
        Sentry.captureException(error, { tags: { feature: 'chat-api' } });
      }
      this.errorService.showError(this.errorMessage);
    }
    return throwError(() => error);
  }
}
